import React, { Fragment } from 'react';
import type { TriggerProps } from '@atlaskit/popup/types';
import Skeleton from '@atlaskit/skeleton';
import { Grid } from '@atlaskit/primitives/grid';
import Flex from '@atlaskit/primitives/components/flex';
import { xcss } from '@atlaskit/primitives/xcss';
import { token } from '@atlaskit/tokens';
import { AppSwitcher as AkAppSwitcherNav4 } from '@atlaskit/navigation-system/top-nav-items';
import { JiraPopup as Popup } from '@atlassian/jira-popup/src/ui/jira-popup.tsx';
import { SwitchToTooltipText } from '@atlassian/switcher/switch-to-tooltip-text';

/**
 * AppSwitcherPlaceholderWithLoadingPopup is designed to show as a fallback,
 * while the real AppSwitcher component is loading.
 */
export function AppSwitcherPlaceholderWithLoadingPopup(): React.JSX.Element {
	return (
		<Popup
			content={() => <SkeletonContent />}
			isOpen
			// eslint-disable-next-line jira/choreographer/message-id-by-folder-structure
			messageId="navigation-apps-topnav4-app-switcher.ui.trigger.popup.loading.popup"
			messageType="transactional"
			placement="bottom-start"
			shouldRenderToParent
			trigger={Trigger}
		/>
	);
}

function Trigger(triggerProps: TriggerProps) {
	return (
		<AkAppSwitcherNav4
			{...triggerProps}
			label={SwitchToTooltipText}
			aria-expanded={false}
			aria-haspopup
			interactionName="jira_app_switcher"
		/>
	);
}

function SkeletonContent() {
	return (
		<Grid
			xcss={containerStyles}
			rowGap="space.200"
			columnGap="space.150"
			templateColumns="32px 1fr"
		>
			<SkeletonItems />
		</Grid>
	);
}

function SkeletonItems() {
	return Array.from({ length: 5 }).map((_, index) => (
		<Fragment key={index}>
			<Skeleton width="32px" height="32px" isShimmering borderRadius={token('radius.large')} />
			<Flex justifyContent="center" direction="column">
				<Skeleton width="100%" height="10px" isShimmering />
			</Flex>
		</Fragment>
	));
}

const containerStyles = xcss({
	width: '250px',
	padding: 'space.200',
});
